import React, { useState } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import StarterCta from '../StarterCta';
import { Sprig } from '../home/Florals';

// Building blocks for the light, warm content pages (About Tarot, About Chakras, ...),
// so they read like the home page: open sections, no boxed panels.

/** Gentle fade-in on first view. Skipped for reduced-motion users. */
export const Fade: React.FC<React.PropsWithChildren<{ className?: string }>> = ({ children, className }) => {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div className={className} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, ease: 'easeOut' }}>
      {children}
    </motion.div>
  );
};

type PageHeroProps = { title: string; accent: string; subtitle: string; eyebrow?: string };

/** Page opener: "<title> <accent>" with the accent in lavender. */
export const PageHero: React.FC<React.PropsWithChildren<PageHeroProps>> = ({ title, accent, subtitle, eyebrow = 'Heal with Madhulika', children }) => (
  <section className="relative pt-32 pb-12 px-6 overflow-hidden bg-gradient-to-b from-lavender-100/70 via-cream to-cream text-center">
    <Sprig className="absolute -left-4 top-24 h-64 text-lavender-300 opacity-60 hidden md:block" />
    <Fade className="max-w-3xl mx-auto relative">
      <p className="text-xs uppercase tracking-[0.3em] text-lavender-700 mb-4">{eyebrow}</p>
      <h1 className="font-fraunces text-4xl sm:text-6xl font-medium leading-[1.05] text-plum mb-5">
        {title} <span className="text-lavender-700">{accent}</span>
      </h1>
      <p className="text-lg text-plum-soft max-w-xl mx-auto">{subtitle}</p>
      {children}
    </Fade>
  </section>
);

type SectionProps = { id: string; title: string; subtitle?: string; tint?: boolean; className?: string };

/** Open section with a centred Fraunces heading. `tint` gives the soft lavender band used on the home page. */
export const Section: React.FC<React.PropsWithChildren<SectionProps>> = ({ id, title, subtitle, tint, className = '', children }) => (
  <section id={id} className={`px-6 py-14 ${tint ? 'bg-lavender-50' : ''} ${className}`}>
    <Fade className="max-w-5xl mx-auto">
      <h2 className="font-fraunces text-3xl sm:text-4xl font-medium text-center mb-2">{title}</h2>
      {subtitle && <p className="text-center text-plum-soft">{subtitle}</p>}
      <div className="mt-10">{children}</div>
    </Fade>
  </section>
);

type Step = { title: string; body: string };

/** Numbered steps laid out in columns, as in "How we begin" on the home page. */
export const Steps: React.FC<{ steps: Step[] }> = ({ steps }) => (
  <ol className={`grid gap-x-8 gap-y-8 sm:grid-cols-2 ${steps.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4'}`}>
    {steps.map((s, i) => (
      <li key={s.title}>
        <span className="font-fraunces text-4xl text-lavender-500">{i + 1}</span>
        <div className="h-px w-12 bg-lavender-300 my-3" />
        <h3 className="font-fraunces text-xl mb-1">{s.title}</h3>
        <p className="text-sm text-plum-soft leading-relaxed">{s.body}</p>
      </li>
    ))}
  </ol>
);

/** Quote-style callout: a lavender rule on the left, no box. */
export const Callout: React.FC<React.PropsWithChildren<{ title?: string }>> = ({ title, children }) => (
  <div className="border-l-2 border-lavender-400 pl-6">
    {title && <h3 className="font-fraunces text-xl text-lavender-700 mb-2">{title}</h3>}
    <div className="text-plum-soft leading-relaxed">{children}</div>
  </div>
);

/** FAQ as a simple divided list. One answer open at a time. */
export const Faq: React.FC<{ items: { q: string; a: string }[] }> = ({ items }) => {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="max-w-3xl mx-auto divide-y divide-lavender-200 border-y border-lavender-200">
      {items.map((item, i) => {
        const isOpen = open === i;
        return (
          <div key={item.q}>
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : i)}
              className="w-full py-5 flex items-center justify-between gap-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-lavender-500"
            >
              <span className={`font-fraunces text-lg transition-colors ${isOpen ? 'text-lavender-700' : 'text-plum'}`}>{item.q}</span>
              <ChevronDown className={`w-5 h-5 flex-shrink-0 text-lavender-600 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                  <p className="pb-5 text-plum-soft leading-relaxed">{item.a}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

/** Closing invitation that opens the "Let's begin" form. */
export const ClosingCta: React.FC<{ source: string; title?: string; body?: string }> = ({
  source,
  title = 'Ready when you are',
  body = 'Share what is on your mind, and Madhulika will take it from there.',
}) => (
  <section className="relative px-6 py-14 text-center overflow-hidden bg-lavender-50">
    <Sprig className="absolute -right-4 bottom-0 h-48 text-lavender-300 opacity-60 hidden md:block" />
    <Fade className="max-w-2xl mx-auto relative">
      <h2 className="font-fraunces text-3xl sm:text-4xl font-medium mb-3">{title}</h2>
      <p className="text-plum-soft mb-6">{body}</p>
      <StarterCta source={source} />
    </Fade>
  </section>
);
